import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import QRCode from 'qrcode';

const LocationLogin = ({ onLoginSuccess }) => {
  const [qrCodeDataURL, setQrCodeDataURL] = useState('');
  const [qrSvg, setQrSvg] = useState('');
  const [loginUrl, setLoginUrl] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [isPolling, setIsPolling] = useState(false);
  const [deviceId, setDeviceId] = useState('');
  const intervalRef = useRef(null);

  useEffect(() => {
    generateLoginQR();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (sessionId && !isPolling) {
      startPollingForLogin();
    }
  }, [sessionId]); // Only depend on sessionId, not isPolling

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  const generateLoginQR = async () => {
    try {
      // Generate a unique session ID for this login attempt
      const newSessionId = Math.random().toString(36).substring(2, 15) + 
                          Math.random().toString(36).substring(2, 15);
      setSessionId(newSessionId);

      // Ensure this device has a stable device_id cookie
      let newDeviceId = '';
      try {
        const r = await fetch('/api/device-id');
        const j = await r.json();
        newDeviceId = j.device_id || '';
        setDeviceId(newDeviceId);
      } catch (e) {
        console.warn('Unable to get device_id, falling back to session-only QR');
      }

      // Create login session on server
      await fetch('/api/create-login-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId: newSessionId, device_id: newDeviceId }),
      });

      // Create QR code that points to mobile login page
      const url = newDeviceId
        ? `${window.location.origin}/mobile-login?session=${newSessionId}&device_id=${encodeURIComponent(newDeviceId)}`
        : `${window.location.origin}/mobile-login?session=${newSessionId}`;
      setLoginUrl(url);
      try {
        const dataURL = await QRCode.toDataURL(url, {
          width: 250,
          margin: 2,
          color: { dark: '#333333', light: '#FFFFFF' }
        });
        setQrCodeDataURL(dataURL);
        setQrSvg('');
      } catch (err) {
        console.warn('QR PNG generation failed, trying SVG:', err);
        const svg = await QRCode.toString(url, { type: 'svg', margin: 2, width: 250, color: { dark: '#333333', light: '#FFFFFF' } });
        setQrSvg(svg);
        setQrCodeDataURL('');
      }
    } catch (error) {
      console.error('Error generating login QR code:', error);
      // Try a delayed retry once
      setTimeout(() => {
        generateLoginQR();
      }, 1000);
    }
  };

  const startPollingForLogin = () => {
    if (isPolling || intervalRef.current) return; // Prevent multiple polling instances
    
    setIsPolling(true);
    
    // Poll every 2 seconds
    intervalRef.current = setInterval(async () => {
      try {
        const url = deviceId 
          ? `/api/check-login?session=${sessionId}&device_id=${encodeURIComponent(deviceId)}`
          : `/api/check-login?session=${sessionId}`;
        const response = await fetch(url);
        const data = await response.json();
        
        if (data.authenticated) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          setIsPolling(false);
          onLoginSuccess(data.user);
        }
      } catch (error) {
        console.error('Error checking login status:', error);
      }
    }, 2000);
  };

  // Removed animations to prevent jumping and flickering

  return (
    <div className="location-login">
      <div className="login-content">
        <div className="login-header">
          <h1>🏢 Location Login</h1>
          <p>Scan the QR code with your phone to authenticate this device</p>
        </div>

        <div className="qr-container">
          {qrCodeDataURL && (
            <img 
              src={qrCodeDataURL} 
              alt="Login QR Code"
              className="login-qr-code"
            />
          )}
          {!qrCodeDataURL && qrSvg && (
            <div className="login-qr-code" dangerouslySetInnerHTML={{ __html: qrSvg }} />
          )}
          {!qrCodeDataURL && (
            <div className="qr-fallback">
              <p>Generating QR code...</p>
              {loginUrl && (
                <p>
                  If QR doesn’t appear, open on phone:
                  <br />
                  <a href={loginUrl}>{loginUrl}</a>
                </p>
              )}
            </div>
          )}
        </div>


        {isPolling && (
          <div className="polling-indicator">
            <div className="spinner"></div>
            <p>Waiting for authentication...</p>
          </div>
        )}

        <div className="qr-debug-info" style={{ opacity: 0.6, fontSize: '0.85rem', marginTop: '0.75rem' }}>
          {sessionId && <div>Session: {sessionId.slice(0,8)}…</div>}
          {deviceId && <div>Device: {deviceId.slice(0,8)}…</div>}
        </div>

      </div>
    </div>
  );
};

export default LocationLogin;
